import { motion } from "framer-motion";
import { Truck, Clock, Leaf, MapPin } from "lucide-react";

interface LogisticsSectionProps {
  warehouseImage: string;
}

const LogisticsSection = ({ warehouseImage }: LogisticsSectionProps) => {
  const advantages = [
    { icon: MapPin, label: "Circuits courts" },
    { icon: Clock, label: "Livraison 24-48h" },
    { icon: Truck, label: "Stock France" },
    { icon: Leaf, label: "Éco-responsable" },
  ];

  return (
    <section className="py-24 bg-gradient-to-b from-background via-primary/5 to-background overflow-hidden">
      <div className="container mx-auto px-4">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          {/* Text Content */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            {/* Title */}
            <div className="mb-10">
              <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-bold text-foreground tracking-wide">
                <span className="text-gradient-blue">LOGISTIQUE</span>
              </h2>
              <div className="w-24 h-1 bg-primary rounded-full mt-4" />
            </div>

            {/* Two Column Text */}
            <div className="grid md:grid-cols-2 gap-8 mb-10">
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                viewport={{ once: true }}
                className="text-muted-foreground leading-relaxed"
              >
                L'optimisation des processus grâce à des actions efficaces, telles que 
                la réduction des matériaux utilisés pour la fabrication et le transport, 
                favorise de grands avantages pour les entreprises et l'environnement. 
                AMS Distrib privilégie les <strong className="text-primary">circuits courts</strong> pour 
                minimiser notre empreinte carbone.
              </motion.p>

              <motion.p
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.4 }}
                viewport={{ once: true }}
                className="text-muted-foreground leading-relaxed"
              >
                La protection des ressources naturelles grâce à l'utilisation de matières 
                premières renouvelables. Nous nous engageons à continuer à aider les 
                entreprises à répondre à leurs besoins durables tout en garantissant 
                une <strong className="text-primary">livraison rapide et efficace</strong> depuis 
                nos entrepôts en France.
              </motion.p>
            </div>

            {/* Advantages Tags */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.6 }}
              viewport={{ once: true }}
              className="flex flex-wrap gap-4"
            >
              {advantages.map((item, index) => (
                <motion.div
                  key={item.label}
                  initial={{ opacity: 0, scale: 0.9 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.4, delay: 0.6 + index * 0.1 }}
                  viewport={{ once: true }}
                  whileHover={{ scale: 1.05 }}
                  className="flex items-center gap-2 px-4 py-2 rounded-full border border-primary/30 bg-primary/5 hover:bg-primary/10 transition-colors"
                >
                  <item.icon className="w-4 h-4 text-primary" />
                  <span className="text-sm font-medium text-foreground">{item.label}</span>
                </motion.div>
              ))}
            </motion.div>
          </motion.div>

          {/* Image */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            viewport={{ once: true }}
            className="relative"
          >
            <div className="absolute -inset-4 bg-gradient-to-br from-primary/20 to-eco/10 rounded-2xl blur-xl" />
            <img
              src={warehouseImage}
              alt="Entrepôt logistique AMS Distrib"
              className="relative w-full h-[400px] lg:h-[500px] object-cover rounded-xl shadow-float"
            />
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default LogisticsSection;